import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, Clock, Bot, Film, CheckCircle, XCircle, Download, Play, RotateCcw, ChevronDown, ChevronUp, Sparkles, Send, Copy, Check } from 'lucide-react';

const API = window.__API_BASE + '/api';

const STATUS_CONFIG = {
  pending:    { icon: Clock,       label: 'Queued — Preparing your video', color: 'text-gray-400',   pct: 5 },
  claimed:    { icon: Bot,         label: 'Picked up — Generation starting', color: 'text-yellow-400', pct: 15 },
  processing: { icon: Film,        label: 'Generating Video...', color: 'text-teal-400',  pct: 60 },
  completed:  { icon: CheckCircle, label: 'Video Ready', color: 'text-green-400', pct: 100 },
  failed:     { icon: XCircle,     label: 'Failed', color: 'text-red-400',   pct: 0 },
};

export default function MontageOutputView({ outputs, seedanceJobId, onDownload, resolveUrl, ImageComponent, onVideoReady, onRerun, processingInfo, scenePrompt, onPromptChange }) {
  const [job, setJob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const resolve = resolveUrl || (u => u);
  const Img = ImageComponent || 'img';

  // Separate outputs by type
  const refImages = (outputs || []).filter(o => o.type === 'image' && !o.label?.includes('Composite'));
  const compositeImg = (outputs || []).find(o => o.type === 'image' && o.label?.includes('Composite'));
  const videoOutput = (outputs || []).find(o => o.type === 'video');
  const enhancedPrompt = (outputs || []).find(o => o.type === 'text' && o.label === 'Enhanced Prompt');

  // Determine if we're in "processing" mode (no video yet, passed via prop)
  const isProcessing = !!processingInfo && !videoUrl;

  // If there's already a video in outputs (Hypereal direct), use it immediately
  useEffect(() => {
    if (videoOutput?.url) {
      setVideoUrl(resolve(videoOutput.url));
      onVideoReady?.();
    }
  }, [videoOutput]);

  // Poll job status only if no video and we have a seedance job ID (Claude Desktop fallback)
  useEffect(() => {
    if (videoUrl || !seedanceJobId) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/seedance/job-status/${seedanceJobId}`);
        setJob(res.data);
        if (res.data.status === 'completed' && res.data.output?.video_url) {
          setVideoUrl(res.data.output.video_url);
          onVideoReady?.();
          clearInterval(pollRef.current);
        } else if (res.data.status === 'failed') {
          clearInterval(pollRef.current);
        }
      } catch { /* silent */ }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [seedanceJobId]);

  const status = videoUrl ? 'completed' : (isProcessing ? 'processing' : (job?.status || (seedanceJobId ? 'pending' : 'completed')));
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;

  const displayLabel = isProcessing && processingInfo?.currentNode
    ? processingInfo.currentNode
    : config.label;
  const displayPct = isProcessing && processingInfo?.progressPct !== undefined
    ? processingInfo.progressPct
    : config.pct;

  return (
    <div data-testid="montage-output-view">
      {/* Video Area */}
      <div
        className="rounded-xl overflow-hidden mb-3"
        style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        {videoUrl ? (
          <div className="relative">
            <video
              src={videoUrl}
              controls
              autoPlay
              className="w-full aspect-video bg-black"
              data-testid="montage-video-player"
            />
            <div className="p-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                <span className="text-xs text-green-400 font-medium">Video Ready</span>
              </div>
              {onDownload && (
                <button
                  onClick={() => onDownload(videoUrl, 'montage-video.mp4')}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] bg-white/[0.06] hover:bg-white/[0.1] text-white/60 hover:text-white/80 transition-colors"
                  data-testid="montage-download-video"
                >
                  <Download className="w-3 h-3" /> Download
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="aspect-video flex flex-col items-center justify-center gap-3 relative">
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute inset-0 opacity-[0.03]"
                style={{
                  backgroundImage: 'linear-gradient(45deg, white 25%, transparent 25%, transparent 75%, white 75%)',
                  backgroundSize: '20px 20px',
                }}
              />
            </div>

            {status === 'processing' ? (
              <Loader2 className="w-10 h-10 text-teal-400/40 animate-spin" />
            ) : status === 'failed' ? (
              <XCircle className="w-10 h-10 text-red-400/40" />
            ) : (
              <Play className="w-10 h-10 text-white/10" />
            )}

            <div className="flex items-center gap-2">
              {status === 'processing' ? (
                <Loader2 className={`w-3.5 h-3.5 ${config.color} animate-spin`} />
              ) : (
                <Icon className={`w-3.5 h-3.5 ${config.color}`} />
              )}
              <span className={`text-sm font-medium ${config.color}`}>{displayLabel}</span>
            </div>

            {status !== 'failed' && status !== 'completed' && (
              <div className="w-48 bg-white/5 rounded-full h-1">
                <div
                  className="h-1 rounded-full transition-all duration-700"
                  style={{ width: `${displayPct}%`, background: 'linear-gradient(90deg, #22D3EE, #14B8A6)' }}
                />
              </div>
            )}

            {job?.status_note && (
              <p className="text-[10px] text-white/40">{job.status_note}</p>
            )}

            {status === 'failed' && job?.error && (
              <p className="text-xs text-red-400/70 max-w-sm text-center">{job.error}</p>
            )}
          </div>
        )}
      </div>

      {/* Reference Thumbnails */}
      {refImages.length > 0 && (
        <div className="flex gap-2 mb-3" data-testid="montage-ref-thumbnails">
          {refImages.map((img, i) => (
            <div
              key={i}
              className="flex-1 rounded-lg overflow-hidden group cursor-pointer relative"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
              onClick={() => onDownload ? onDownload(resolve(img.url), img.label?.replace(/[^a-zA-Z0-9.-]/g, '_') + '.png') : window.open(resolve(img.url), '_blank')}
            >
              <Img
                src={resolve(img.url)}
                alt={img.label}
                className="w-full aspect-[3/4] object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 active:bg-black/30 transition-colors flex items-center justify-center">
                <Download className="w-5 h-5 text-white opacity-0 group-hover:opacity-80 group-active:opacity-80 transition-opacity" />
              </div>
              <div className="px-1.5 py-1">
                <span className="text-[8px] text-white/40">{img.label?.replace(' Reference', '')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Scene Prompt — editable for re-run with custom prompt */}
      {(scenePrompt !== undefined) && (
        <div className="mb-3 rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="px-3 py-2">
            <label className="text-[10px] text-white/40 block mb-1.5">Override Prompt <span className="text-[8px] text-white/20">(optional — leave empty to auto-generate)</span></label>
            <textarea
              value={scenePrompt || ''}
              onChange={(e) => onPromptChange?.(e.target.value)}
              rows={2}
              className="w-full px-2.5 py-2 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] text-white/80 placeholder-white/20 focus:outline-none focus:border-white/[0.15] resize-none"
              placeholder="Leave empty for Style Engine auto-prompt, or type a custom prompt..."
              data-testid="montage-scene-prompt"
            />
          </div>
          {/* Re-run button */}
          {onRerun && !isProcessing && (
            <div className="px-3 pb-2.5">
              <button
                onClick={onRerun}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-all"
                style={{ background: 'linear-gradient(135deg, rgba(20,184,166,0.15), rgba(34,211,238,0.15))', border: '1px solid rgba(20,184,166,0.2)', color: '#5EEAD4' }}
                data-testid="montage-rerun-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Re-run
              </button>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Prompt (collapsible) — from completed run */}
      {enhancedPrompt?.content && (
        <div className="mb-3 rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={() => setShowPrompt(!showPrompt)}
            className="flex-1 px-3 py-2 flex items-center justify-between text-left"
            data-testid="montage-toggle-prompt"
          >
            <span className="text-[10px] text-white/40">Generated Prompt</span>
            {showPrompt ? <ChevronUp className="w-3 h-3 text-white/30" /> : <ChevronDown className="w-3 h-3 text-white/30" />}
          </button>
          {showPrompt && (
            <div className="px-3 pb-2.5">
              <div className="flex justify-end mb-1">
                <button
                  onClick={() => { navigator.clipboard.writeText(enhancedPrompt.content); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] text-white/40 hover:text-white/60 hover:bg-white/[0.05] transition-all"
                  data-testid="copy-prompt-btn"
                >
                  {copied ? <><Check className="w-3 h-3 text-green-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <p className="text-[11px] text-white/50 leading-relaxed">{enhancedPrompt.content}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
